import type { Feature, Polygon, MultiPolygon } from "geojson";
import { mergePolygons, mergeHoles } from "../../../domain/geometry/operations/merge";
import type { GeometryResult, AnyPolygonFeature } from "../../../domain/geometry/types";
import type { UndoableCommand } from "../undoStack";

export interface MergePolygonsResult {
	command: UndoableCommand;
	merged: AnyPolygonFeature;
}

export function mergePolygonsUseCase(
	polyA: Feature<Polygon>,
	polyB: Feature<Polygon>,
	applyResult: (merged: AnyPolygonFeature) => void,
	revert: (a: Feature<Polygon>, b: Feature<Polygon>) => void,
): GeometryResult<MergePolygonsResult> {
	const result = mergePolygons(polyA, polyB);
	if (!result.ok) return result;

	const coords = result.data.coordinates;
	let merged: AnyPolygonFeature;
	if (coords.length === 1) {
		const geom: Polygon = { type: "Polygon", coordinates: coords[0] };
		merged = { type: "Feature", properties: {}, geometry: geom };
	} else {
		const geom: MultiPolygon = { type: "MultiPolygon", coordinates: coords };
		merged = { type: "Feature", properties: {}, geometry: geom };
	}

	return {
		ok: true,
		data: {
			merged,
			command: {
				label: "Merge polygons",
				execute: () => applyResult(merged),
				undo: () => revert(polyA, polyB),
			},
		},
	};
}

export interface MergeHolesResult {
	command: UndoableCommand;
	merged: Feature<Polygon>;
}

export function mergeHolesUseCase(
	holeA: Feature<Polygon>,
	holeB: Feature<Polygon>,
	applyResult: (merged: Feature<Polygon>) => void,
	revert: (a: Feature<Polygon>, b: Feature<Polygon>) => void,
): GeometryResult<MergeHolesResult> {
	const result = mergeHoles(holeA, holeB);
	if (!result.ok) return result;

	const merged: Feature<Polygon> = {
		type: "Feature",
		properties: {},
		geometry: { type: "Polygon", coordinates: [result.data] },
	};

	return {
		ok: true,
		data: {
			merged,
			command: {
				label: "Merge holes",
				execute: () => applyResult(merged),
				undo: () => revert(holeA, holeB),
			},
		},
	};
}
